"use client";

import { Layers, List } from "lucide-react";

import { FormFieldStatus } from "@punchup/ui-common/components/common";
import { VenuePagesRow } from "@punchup/data";
import { cn } from "@punchup/utils/cn";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { updateVenueShowsGroupingMode } from "../../data/venue_pages";
import { useState } from "react";

interface Props {
  venuePage: VenuePagesRow;
}

type GroupingMode = VenuePagesRow["shows_grouping_mode"];

const OPTIONS: {
  mode: GroupingMode;
  label: string;
  icon: typeof List;
}[] = [
  { mode: "individual", label: "Individual events", icon: List },
  { mode: "grouped", label: "Group series", icon: Layers },
];

/**
 * Venue-manager toggle for how the public shows grid renders:
 *  - individual: one card per show (default)
 *  - grouped: collapse shows that share a show-group into one card that drills
 *    into the existing /shows/{slug} page.
 */
export default function VenueShowsGroupingSetting({ venuePage }: Props) {
  const [groupingMode, setGroupingMode] = useState<GroupingMode>(
    venuePage.shows_grouping_mode
  );
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supabase = createClientComponentClient();

  const handleUpdate = async (mode: GroupingMode) => {
    if (mode === groupingMode) return;
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      await updateVenueShowsGroupingMode({
        supabase,
        venuePageId: venuePage.id,
        newShowsGroupingMode: mode,
      });
      setGroupingMode(mode);
      setSuccess(true);
    } catch (err) {
      console.error("[VenueShowsGroupingSetting] Error:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-6">
      <div className="flex flex-row gap-x-1 rounded-md bg-[#f7f7f7]">
        {OPTIONS.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            onClick={() => handleUpdate(mode)}
            disabled={loading}
            className={cn(
              "flex flex-row items-center gap-x-2 rounded-md p-2 transition-colors",
              groupingMode === mode
                ? "stroke-white bg-secondary-blue text-white"
                : "stroke-secondary-blue bg-[#f7f7f7] text-secondary-blue"
            )}
          >
            <Icon height={24} width={24} className="stroke-inherit" />
            <span className="font-semibold text-inherit">{label}</span>
          </button>
        ))}
      </div>
      <p className="text-sm font-light text-secondary-blue">
        {groupingMode === "grouped"
          ? "Multiple shows for the same series collapse into one card (e.g. “Dedrick Flynn · Aug 1–2”) that opens the full list of dates."
          : "Every show is listed on its own card."}
      </p>
      <FormFieldStatus
        loading={loading}
        loadingMessage="Updating shows layout..."
        success={success}
        successMessage="Shows layout updated!"
        error={Boolean(error)}
        errorMessage={error ?? ""}
      />
    </div>
  );
}
